// This is a rewrite of https://github.com/guerrillacontra/html5-es6-physics-rope
var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var _a;
import { AnimationFrameController } from '@folkjs/canvas';
import { css, property } from '@folkjs/dom/ReactiveElement';
import * as V from '@folkjs/geometry/Vector2';
import { FolkBaseConnection } from './folk-base-connection';
export class FolkRope extends FolkBaseConnection {
    constructor() {
        super(...arguments);
        // TODO: stop simulation when energy approaches 0
        this.#rAF = new AnimationFrameController(this, 10000);
        this.#svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
        this.#path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
        this.#path2 = document.createElementNS('http://www.w3.org/2000/svg', 'path');
        this.#points = [];
        this.gravity = { x: 0, y: 3000 };
        this.#cutIndex = -1;
    }
    static { this.tagName = 'folk-rope'; }
    static #resolution = 5;
    static { this.styles = css `
    :host {
      display: block;
      position: absolute;
      inset: 0;
      pointer-events: none;
    }

    svg {
      height: 100%;
      pointer-events: none;
      width: 100%;
    }

    path {
      fill: none;
      pointer-events: stroke;
      stroke: var(--folk-rope-color, black);
      stroke-width: var(--folk-rope-width, 3);
      stroke-linecap: var(--folk-rope-linecap, round);
    }
  `; }
    // TODO: stop simulation when energy approaches 0
    #rAF;
    #svg;
    #path;
    #path2;
    #points;
    get points() {
        return this.#points;
    }
    createRenderRoot() {
        const root = super.createRenderRoot();
        this.#svg.append(this.#path, this.#path2);
        root.appendChild(this.#svg);
        return root;
    }
    tick() {
        for (const point of this.#points) {
            this.#integratePoint(point, this.gravity);
        }
        // 3 constraint iterations is enough for fixed timestep
        for (let iteration = 0; iteration < 3; iteration++) {
            for (const point of this.#points) {
                this.#constrainPoint(point);
            }
        }
    }
    update(changedProperties) {
        super.update(changedProperties);
        const { sourceRect, targetRect } = this;
        if (sourceRect === null || targetRect === null) {
            this.#rAF.stop();
            this.#points = [];
            this.#path.removeAttribute('d');
            this.#path2.removeAttribute('d');
            return;
        }
        const source = {
            x: sourceRect.x + sourceRect.width / 2,
            y: sourceRect.y + sourceRect.height,
        };
        const target = {
            x: targetRect.x + targetRect.width / 2,
            y: targetRect.y + targetRect.height,
        };
        if (this.#points.length === 0) {
            this.#points = this.#generatePoints(source, target);
            this.#rAF.start();
        }
        const startingPoint = this.#points.at(0);
        const endingPoint = this.#points.at(-1);
        if (startingPoint === undefined || endingPoint === undefined)
            return;
        startingPoint.pos = source;
        endingPoint.pos = target;
    }
    /** add/remove points based on distance between source and target rects */
    stretch() {
        if (this.sourceRect === null || this.targetRect === null || this.#points.length < 2)
            return;
        // Calculate desired length based on source and target positions
        const distance = V.distance(this.sourceRect, this.targetRect);
        // Apply a nonlinear scaling to gradually increase point spacing with distance
        const scaleFactor = Math.log10(distance + 10) / 2; // adjust the '+10' and '/2' to tune the curve
        const effectiveResolution = _a.#resolution * scaleFactor;
        const desiredPoints = Math.floor(distance / effectiveResolution);
        while (this.#points.length < desiredPoints) {
            const lastPoint = this.#points.at(-1);
            lastPoint.isFixed = false;
            const newPoint = {
                pos: { ...lastPoint.pos },
                oldPos: { ...lastPoint.pos },
                distanceToNextPoint: _a.#resolution,
                mass: 1,
                damping: 0.99,
                velocity: V.zero(),
                isFixed: true,
                prev: lastPoint,
                next: null,
            };
            lastPoint.next = newPoint;
            this.#points.push(newPoint);
        }
        while (this.#points.length > desiredPoints) {
            this.#points.pop();
            this.#points.at(-1).isFixed = true;
        }
    }
    render() {
        if (this.#points.length < 2)
            return;
        let pathData = `M ${this.#points[0].pos.x} ${this.#points[0].pos.y}`;
        let path2Data = '';
        let isBroken = false;
        for (let i = 1; i < this.#points.length; i++) {
            const point = this.#points[i];
            if (point.prev === null) {
                isBroken = true;
                path2Data = `M ${point.pos.x} ${point.pos.y}`;
            }
            else if (isBroken) {
                path2Data += ` L ${point.pos.x} ${point.pos.y}`;
            }
            else {
                pathData += ` L ${point.pos.x} ${point.pos.y}`;
            }
        }
        this.#path.setAttribute('d', pathData);
        if (path2Data) {
            this.#path2.setAttribute('d', path2Data);
        }
        else {
            this.#path2.removeAttribute('d');
        }
    }
    #generatePoints(start, end) {
        const delta = V.subtract(end, start);
        const len = V.magnitude(delta);
        const points = [];
        const pointsLen = Math.floor(len / _a.#resolution);
        for (let i = 0; i < pointsLen; i++) {
            const percentage = i / (pointsLen - 1);
            const pos = V.lerp(start, end, percentage);
            points.push({
                pos,
                oldPos: { ...pos },
                distanceToNextPoint: _a.#resolution,
                mass: 1,
                damping: 0.99,
                velocity: V.zero(),
                isFixed: i === 0 || i === pointsLen - 1,
                prev: null,
                next: null,
            });
        }
        // Link nodes into a doubly linked list
        for (let i = 0; i < pointsLen; i++) {
            const prev = i != 0 ? points[i - 1] : null;
            const curr = points[i];
            const next = i != pointsLen - 1 ? points[i + 1] : null;
            curr.prev = prev;
            curr.next = next;
        }
        return points;
    }
    #integratePoint(point, gravity) {
        if (!point.isFixed) {
            point.velocity = V.subtract(point.pos, point.oldPos);
            point.oldPos = { ...point.pos };
            const accel = V.add(gravity, { x: 0, y: point.mass });
            const tsSq = this.#rAF.fixedTimestep ** 2;
            point.pos.x += point.velocity.x * point.damping + accel.x * tsSq;
            point.pos.y += point.velocity.y * point.damping + accel.y * tsSq;
        }
        else {
            point.velocity = V.zero();
            point.oldPos = { ...point.pos };
        }
    }
    // Apply constraints related to other nodes next to it (keeps each node within distance)
    #constrainPoint(point) {
        if (point.next)
            applyConstraint(point, point.next);
        if (point.prev)
            applyConstraint(point, point.prev);
    }
    #cutIndex;
    get isCut() {
        return this.#cutIndex !== -1;
    }
    cut(atPercentage = 0.5) {
        if (this.isCut)
            return;
        this.#cutIndex = this.#getPointIndexAt(atPercentage);
        this.#points[this.#cutIndex].next = null;
        this.#points[this.#cutIndex + 1].prev = null;
        this.#rAF.reset();
    }
    mend() {
        if (!this.isCut)
            return;
        this.#points[this.#cutIndex].next = this.#points[this.#cutIndex + 1];
        this.#points[this.#cutIndex + 1].prev = this.#points[this.#cutIndex];
        this.#cutIndex = -1;
        this.#rAF.reset();
    }
    getPointAt(percentage) {
        return this.#points[this.#getPointIndexAt(percentage)];
    }
    #getPointIndexAt(percentage) {
        const clamped = Math.min(Math.max(percentage, 0), 1);
        return Math.floor(this.#points.length * clamped);
    }
    getPercentageFromPoint(hitPoint) {
        for (let i = 0; i < this.#points.length - 1; i++) {
            const point = this.#points[i];
            const nextPoint = point.next;
            if (nextPoint === null)
                return null;
            if (V.distance(point.pos, hitPoint) + V.distance(hitPoint, nextPoint.pos) - V.distance(point.pos, nextPoint.pos) <
                1) {
                return i / this.#points.length;
            }
        }
        return null;
    }
}
_a = FolkRope;
__decorate([
    property({ type: Object })
], FolkRope.prototype, "gravity", void 0);
function applyConstraint(p1, p2) {
    const delta = V.subtract(p2.pos, p1.pos);
    const len = V.magnitude(delta);
    // Prevent division by zero
    if (len < 0.0001)
        return;
    const diff = len - p1.distanceToNextPoint;
    const normal = V.normalized(delta);
    const adjustment = V.scale(normal, diff * 0.75);
    if (!p1.isFixed) {
        p1.pos = V.add(p1.pos, adjustment);
    }
    if (!p2.isFixed) {
        p2.pos = V.subtract(p2.pos, adjustment);
    }
}
//# sourceMappingURL=folk-rope.js.map